import * as React from 'react'
import { Loader2 } from 'lucide-react'
import { FieldOverlay } from './FieldOverlay'
import { HtmlDocumentViewer } from './HtmlDocumentViewer'
import type { PDFDocumentProxy } from 'pdfjs-dist'
import type { DocumentDto, FieldDto, FieldValueDto } from '@lib/dto/envelope.dto'
import { DraftDot, QuestionDot } from './QuestionDot'
import type { QuestionLayer } from './QuestionSidebar'
import type { Uuid } from '@lib/validators/params'
import { cn } from '@/lib/utils'
import { usePaper } from '@/theme/ThemeProvider'

type PdfDocument = PDFDocumentProxy

export function PacketViewer({
  documents,
  fields = [],
  values = {},
  viewerSignerId = '',
  signedSignerIds = [],
  scrollToDocumentId,
  onChange,
  onClickSign,
  questions,
  watermark = false,
  titled = true,
  loadDocument,
  loadAsset,
}: {
  documents: readonly DocumentDto[]
  fields?: readonly FieldDto[]
  values?: Record<string, FieldValueDto>
  viewerSignerId?: string
  /** Signers who have signed; their empty fields show as empty, not waiting. */
  signedSignerIds?: readonly string[]
  scrollToDocumentId?: string | undefined
  onChange?: ((field: FieldDto, value: FieldValueDto) => void) | undefined
  onClickSign?: ((field: FieldDto) => void) | undefined
  questions?: QuestionLayer | undefined
  /** Marks every page TEST: a test envelope whose host hides PixelSign's banner. */
  watermark?: boolean
  /** Labels each document with its title; off when the host already shows it. */
  titled?: boolean
  loadDocument: (documentId: string) => Promise<Blob>
  loadAsset: (documentId: string, assetId: string) => Promise<Blob>
}) {
  const pdfDocuments = documents.filter((document) => document.source === 'pdf')
  const [pdfs, setPdfs] = React.useState<Map<string, PdfDocument>>(() => new Map())
  const pdfKey = pdfDocuments.map((document) => document.id).join(',')

  React.useEffect(() => {
    let cancelled = false
    const loaded: PdfDocument[] = []
    async function load(): Promise<void> {
      const toLoad = documents.filter((document) => document.source === 'pdf')
      // pdf.js is most of a megabyte, so it's fetched only when a packet has a
      // PDF, alongside the documents themselves.
      if (toLoad.length === 0) {
        if (!cancelled) {
          setPdfs(new Map())
        }
        return
      }
      const pdfModule = import('./pdf')
      const next = await Promise.all(
        toLoad.map(async (doc) => {
          const [module, blob] = await Promise.all([pdfModule, loadDocument(doc.id)])
          const pdf = await module.pdfjs.getDocument({ data: await blob.arrayBuffer() }).promise
          loaded.push(pdf)
          return { documentId: doc.id, pdf }
        }),
      )
      if (cancelled) {
        await Promise.all(loaded.map((pdf) => pdf.cleanup()))
        return
      }
      setPdfs(new Map(next.map((item) => [item.documentId, item.pdf])))
    }
    void load()
    return () => {
      cancelled = true
      void Promise.all(loaded.map((pdf) => pdf.cleanup()))
    }
  }, [documents, pdfKey, loadDocument])

  React.useEffect(() => {
    if (!scrollToDocumentId) {
      return
    }
    const node = window.document.getElementById(`packet-doc-${scrollToDocumentId}`)
    node?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [scrollToDocumentId])

  const pdfsReady = pdfDocuments.length === 0 || pdfs.size === pdfDocuments.length
  if (!pdfsReady) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    )
  }

  function documentBody(document: DocumentDto): React.ReactNode {
    switch (document.source) {
      case 'html':
        return (
          <HtmlDocumentViewer
            documentId={document.id}
            fields={fields.filter((field) => field.documentId === document.id)}
            values={values}
            viewerSignerId={viewerSignerId}
            signedSignerIds={signedSignerIds}
            onChange={onChange}
            onClickSign={onClickSign}
            questions={questions}
            watermark={watermark}
            loadDocument={loadDocument}
            loadAsset={loadAsset}
          />
        )
      case 'pdf':
        return (
          <PdfPages
            pdf={pdfs.get(document.id)}
            documentId={document.id}
            fields={fields}
            values={values}
            viewerSignerId={viewerSignerId}
            signedSignerIds={signedSignerIds}
            onChange={onChange}
            onClickSign={onClickSign}
            questions={questions}
            watermark={watermark}
          />
        )
      default: {
        const unexpected: never = document.source
        throw new Error(`Unexpected document source ${String(unexpected)}`)
      }
    }
  }

  const ordered = [...documents].sort(
    (a, b) => a.presentationOrder - b.presentationOrder,
  )

  return (
    <div className="space-y-6">
      {ordered.map((document) => (
        <section key={document.id} id={`packet-doc-${document.id}`} className="space-y-2">
          {titled ? <p className="text-xs text-muted-foreground">{document.title}</p> : null}
          {documentBody(document)}
        </section>
      ))}
    </div>
  )
}

function PdfPages({
  pdf,
  documentId,
  fields,
  values,
  viewerSignerId,
  signedSignerIds,
  onChange,
  onClickSign,
  questions,
  watermark,
}: {
  pdf: PdfDocument | undefined
  documentId: Uuid
  fields: readonly FieldDto[]
  values: Record<string, FieldValueDto>
  viewerSignerId: string
  signedSignerIds: readonly string[]
  onChange?: ((field: FieldDto, value: FieldValueDto) => void) | undefined
  onClickSign?: ((field: FieldDto) => void) | undefined
  questions?: QuestionLayer | undefined
  watermark: boolean
}) {
  if (!pdf) {
    return null
  }
  return (
    <div className="space-y-4">
      {Array.from({ length: pdf.numPages }, (_, index) => {
        const page = index + 1
        return (
          <div key={`${documentId}-${String(page)}`} className="space-y-2">
            <p className="text-xs text-muted-foreground">
              Page {page} of {pdf.numPages}
            </p>
            <PdfPageCanvas
              pdf={pdf}
              pageNumber={page}
              watermark={watermark}
              onAsk={
                questions?.onAskPin
                  ? (x, y) => {
                      const ask = questions.onAskPin
                      if (!ask) {
                        return
                      }
                      ask({ documentId, page, x, y, quote: null })
                    }
                  : undefined
              }
            >
              {(size) => (
                <>
                  {fields
                    .filter(
                      (field) => field.documentId === documentId && field.page === page,
                    )
                    .map((field) => (
                      <FieldOverlay
                        key={field.id}
                        field={field}
                        value={values[field.id]}
                        page={size}
                        viewerSignerId={viewerSignerId}
                        signerSigned={signedSignerIds.includes(field.signerId)}
                        onChange={(value) => onChange?.(field, value)}
                        onClickSign={() => onClickSign?.(field)}
                      />
                    ))}
                  {questions?.threads
                    .filter((thread) => thread.documentId === documentId && thread.page === page)
                    .map((thread) => (
                      <QuestionDot
                        key={thread.id}
                        thread={thread}
                        selected={thread.id === questions.selectedThreadId}
                        onSelect={questions.onSelectThread}
                      />
                    ))}
                  {questions?.draft &&
                  questions.draft.documentId === documentId &&
                  questions.draft.page === page ? (
                    <DraftDot x={questions.draft.x} y={questions.draft.y} />
                  ) : null}
                </>
              )}
            </PdfPageCanvas>
          </div>
        )
      })}
    </div>
  )
}

function PdfPageCanvas({
  pdf,
  pageNumber,
  children,
  onAsk,
  watermark,
}: {
  pdf: PdfDocument
  pageNumber: number
  watermark: boolean
  children: (size: { widthPts: number; heightPts: number }) => React.ReactNode
  onAsk?: ((x: number, y: number) => void) | undefined
}) {
  const canvasRef = React.useRef<HTMLCanvasElement>(null)
  const paper = usePaper()
  const [size, setSize] = React.useState<{
    widthPts: number
    heightPts: number
  } | null>(null)

  React.useEffect(() => {
    let cancelled = false
    const canvas = canvasRef.current
    if (!canvas) {
      return
    }
    void (async () => {
      const page = await pdf.getPage(pageNumber)
      const viewport = page.getViewport({ scale: 1.4 })
      if (cancelled) {
        return
      }
      canvas.width = viewport.width
      canvas.height = viewport.height
      const context = canvas.getContext('2d')
      if (!context) {
        return
      }
      await page.render({ canvasContext: context, viewport, canvas }).promise
      if (!cancelled) {
        setSize({
          widthPts: viewport.width / 1.4,
          heightPts: viewport.height / 1.4,
        })
      }
    })()
    return () => {
      cancelled = true
    }
  }, [pdf, pageNumber])

  return (
    <div
      className={cn('relative overflow-hidden rounded-md border border-border bg-card', onAsk && 'cursor-crosshair', paper)}
      onClick={(event) => {
        if (!onAsk) {
          return
        }
        const target = event.target
        if (!(target instanceof Element) || target.closest('button, input, select, textarea, label')) {
          return
        }
        const rect = event.currentTarget.getBoundingClientRect()
        if (rect.width === 0 || rect.height === 0) {
          return
        }
        onAsk(
          ((event.clientX - rect.left) / rect.width) * 100,
          ((event.clientY - rect.top) / rect.height) * 100,
        )
      }}
    >
      <canvas ref={canvasRef} className="block h-auto w-full" />
      {watermark ? <div aria-hidden="true" className="sign-test-watermark absolute inset-0" /> : null}
      {size ? <div className="absolute inset-0">{children(size)}</div> : null}
    </div>
  )
}
